import { useMsal } from '@azure/msal-react'
import { Box, Button, Group, Stack } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import {
  StripePaymentElementChangeEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSessionInfo } from '../../contexts/SessionContext'
import { confirmPayment } from '../../data/checkout.service'
import { Totals } from '../../models/Checkout/Totals'
import CartSummary from './CartSummary'

interface CheckoutFormProps {
  totals: Totals
  discount?: number
  bundleTotal?: number
  hasCheckoutItems: boolean
}

export const CheckoutForm = (props: CheckoutFormProps) => {
  const { totals, discount, bundleTotal, hasCheckoutItems } = props
  const { session, setSession } = useSessionInfo()
  const stripe = useStripe()
  const elements = useElements()
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [isPaymentConfigured, setIsPaymentConfigured] = useState(false)
  const navigate = useNavigate()
  const { instance } = useMsal()
  const isMobile = useMediaQuery('(max-width: 768px)')

  const handleError = (error: any) => {
    console.log(error.message)
    setIsLoadingSubmit(false)
  }

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return
    }
    setIsLoadingSubmit(true)

    const { error: submitError } = await elements.submit()
    if (submitError) {
      handleError(submitError)
      return
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    })
    if (error) {
      handleError(error)
    }

    if (paymentIntent && paymentIntent.status === 'requires_capture') {
      await confirmPayment(paymentIntent.id, instance)
      if (session) {
        setSession({ ...session, itemsInCart: [], allLocationsBundleCount: 0 })
      }
      navigate('/dashboard/myAds')
      notifications.show({
        title: 'Payment successful',
        message: 'Your ads will be up and running shortly',
        color: 'green',
      })
    }

    setIsLoadingSubmit(false)
  }

  const handlePaymentElementChange = (
    event: StripePaymentElementChangeEvent,
  ) => {
    setIsPaymentConfigured(event.complete)
  }

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'accordion',
      radios: false,
    },
  }

  return isMobile ? (
    <Stack gap={20}>
      <Box>
        <CartSummary
          totals={totals}
          discount={discount}
          bundleTotal={bundleTotal}
          isButtonDisabled={
            !hasCheckoutItems || isLoadingSubmit || !isPaymentConfigured
          }
          buttonText='Place Order'
          onSubmit={handleSubmit}
          isLoadingSubmit={isLoadingSubmit}
          buttonDisabledText='Please complete payment info'
          hideButton={true}
        />
      </Box>
      <Box>
        <PaymentElement
          options={paymentElementOptions}
          onChange={handlePaymentElementChange}
        />
      </Box>
      <Button
        fullWidth
        size='md'
        radius={12}
        onClick={handleSubmit}
        disabled={
          !hasCheckoutItems || isLoadingSubmit || !isPaymentConfigured
        }
        loading={isLoadingSubmit}
      >
        Place Order
      </Button>
    </Stack>
  ) : (
    <Group gap={50} h='100%' w='100%' align='flex-start'>
      <Box flex={8}>
        <PaymentElement
          options={paymentElementOptions}
          onChange={handlePaymentElementChange}
        />
      </Box>
      <Box flex={2}>
        <CartSummary
          totals={totals}
          discount={discount}
          bundleTotal={bundleTotal}
          isButtonDisabled={
            !hasCheckoutItems || isLoadingSubmit || !isPaymentConfigured
          }
          buttonText='Place Order'
          onSubmit={handleSubmit}
          isLoadingSubmit={isLoadingSubmit}
          buttonDisabledText='Please complete payment info'
        />
      </Box>
    </Group>
  )
}
